import React, { useEffect, useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  getStraightPath,
  EdgeProps,
} from 'reactflow';
import { useOCPStore } from '../hooks/useOCPStore';
import {
  OnboardingFlowTransition,
  getEdgeCodeFromSteps,
} from '@scale/shared/onboardingControlPanel';
import { Tag, Tooltip } from '@scale/scaleui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface FlowEdgeDataType extends OnboardingFlowTransition {
  label: {
    transitionUserCount: number;
    transitionNumber: number;
    transitionNames: string[];
  };
  isFailureNodeTransition: boolean;
}

const FlowEdge = ({
  id,
  data,
  sourceX,
  sourceY,
  targetX,
  targetY,
  markerEnd,
  style,
}: EdgeProps<FlowEdgeDataType>) => {
  const [edgePath, labelX, labelY] = data?.isFailureNodeTransition
    ? getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
      })
    : getStraightPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
      });

  const { detailTarget, userTraversal, validationResults } = useOCPStore(state => ({
    detailTarget: state.detailTarget,
    userTraversal: state.userTraversal,
    validationResults: state.validationResults,
  }));
  const selected =
    (detailTarget as OnboardingFlowTransition)?.sourceNode === data?.sourceNode &&
    (detailTarget as OnboardingFlowTransition)?.destNode === data?.destNode;
  const validationErrors = validationResults.errors.filter(error =>
    error.message.includes(data?.name ?? ''),
  );
  const [edgeTraversed, setEdgeTraversed] = useState<boolean>(false);
  const [edgeUntraversable, setEdgeUntraversable] = useState<boolean>(false);
  const [backgroundColor, setBackgroundColor] = useState<string>('#FFFFFF');
  const [textColor, setTextColor] = useState<string>('black');
  const [opacity, setOpacity] = useState<number>(1);

  useEffect(() => {
    if (userTraversal && data?.sourceNode && data?.destNode) {
      const edgeCode = getEdgeCodeFromSteps(data?.sourceNode, data?.destNode);
      setEdgeTraversed(userTraversal.traversedEdges.includes(edgeCode));
      setEdgeUntraversable(
        userTraversal.unvisitableEdges.includes(edgeCode) &&
          !userTraversal.traversedEdges.includes(edgeCode),
      );
    } else {
      setEdgeTraversed(false);
      setEdgeUntraversable(false);
    }
  }, [data, userTraversal]);

  useEffect(() => {
    if (selected) {
      setBackgroundColor('#6A5DF9');
      setTextColor('white');
    } else if (validationErrors.length) {
      setBackgroundColor('#CC3333');
    } else if (edgeTraversed) {
      setBackgroundColor('#6A5DF9');
      setTextColor('white');
    } else {
      setBackgroundColor('#FFFFFF');
      setTextColor('black');
    }
  }, [edgeTraversed, selected, validationErrors]);

  useEffect(() => {
    if (edgeUntraversable) {
      setOpacity(0.5);
    } else {
      setOpacity(1);
    }
  }, [edgeUntraversable]);

  if (!data) return null;
  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{ ...style, stroke: backgroundColor }}
      />
      <EdgeLabelRenderer>
        <Tooltip
          text={
            <div style={{ width: '20vw' }} className="p-2 flex flex-col">
              <div style={{ display: 'flex', justifyContent: 'space-between' }} className="mb-2">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FontAwesomeIcon icon={['far', 'user']} className="mr-1" />
                  <div className="text-md">{data.label.transitionUserCount}</div>
                </div>
                <div style={{ wordWrap: 'break-word', width: '16vw' }}>
                  {`Contributor(s) who went through this transition in the past 24 hours`}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FontAwesomeIcon icon={['far', 'arrow-right']} className="mr-1" />
                  <div className="text-md">{data.label.transitionNumber}</div>
                </div>
                <div style={{ wordWrap: 'break-word', width: '16vw' }}>
                  {`Transition(s): ${data.label.transitionNames.join(', ')}`}
                </div>
              </div>
            </div>
          }
          target={
            <div
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                background: backgroundColor,
                opacity,
                fontSize: 10,
                padding: '2px 5px',
                color: textColor,
                pointerEvents: 'all',
              }}
              className="nodrag nopan rounded-md border border-neutral-400"
            >
              <Tag variant="white" icon={['far', 'user']}>
                {data.label.transitionUserCount}
              </Tag>
              <div id="divider" className="my-0.5 h-5 border-l border-neutral-400" />
              <Tag variant="white" icon={['far', 'arrow-right']}>
                {data.label.transitionNumber}
              </Tag>
            </div>
          }
        />
      </EdgeLabelRenderer>
    </>
  );
};

export default FlowEdge;
